'use client';

import { c as _c } from "react/compiler-runtime";
import type { ChainId } from '@morpho-org/blue-sdk';
import { safeParseNumber } from '@morpho-org/blue-sdk-viem';
import { Skeleton } from '@morpho-org/ui-v6/server';
import { memo, useMemo } from 'react';
import { type Address, zeroAddress } from 'viem';
import { formatFeePercent, getParsedValue } from '@/helpers/formatters';
import { VaultV2TimelockedFunctionName } from '@/types/enums';
import type { VaultV2PendingTimelock } from '../../types/pendingTimelocks';
import AddressWithCopyAndExplorer from '../../components/AddressWithCopyAndExplorer';
import VaultV2PendingTimelocksIndicator from '../../components/VaultV2PendingTimelocksIndicator';
import { VaultV2RowListRow, VaultV2RowListSection } from '../../components/VaultV2RowList';
interface FeeData {
  fee: number | null;
  recipient: Address | null;
}
interface VaultV2PerformanceFeesProps {
  chainId?: ChainId;
  performanceFeeData?: FeeData;
  managementFeeData?: FeeData;
  pendingTimelocks?: VaultV2PendingTimelock[];
  isLoading?: boolean;
}
const VaultV2PerformanceFees = t0 => {
  const $ = _c(57);
  const {
    chainId,
    performanceFeeData,
    managementFeeData,
    pendingTimelocks,
    isLoading
  } = t0;
  performanceFeeData?.fee;
  let t1;
  if ($[0] !== performanceFeeData?.fee) {
    t1 = formatFeePercent(getParsedValue(performanceFeeData?.fee, safeParseNumber));
    $[0] = performanceFeeData?.fee;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const formattedPerformanceFee = t1;
  let t2;
  if ($[2] !== managementFeeData?.fee) {
    t2 = () => formatFeePercent(getParsedValue(managementFeeData?.fee, safeParseNumber));
    $[2] = managementFeeData?.fee;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  managementFeeData?.fee;
  let t3;
  if ($[4] !== t2) {
    t3 = t2();
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const formattedManagementFee = t3;
  const performanceFeeRecipient = performanceFeeData?.recipient ?? zeroAddress;
  const managementFeeRecipient = managementFeeData?.recipient ?? zeroAddress;
  let t4;
  if ($[6] !== pendingTimelocks) {
    t4 = pendingTimelocks?.filter(_temp) ?? [];
    $[6] = pendingTimelocks;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const performanceFeePendingTimelocks = t4;
  let t5;
  if ($[8] !== pendingTimelocks) {
    t5 = pendingTimelocks?.filter(_temp2) ?? [];
    $[8] = pendingTimelocks;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const managementFeePendingTimelocks = t5;
  let t6;
  if ($[10] !== pendingTimelocks) {
    t6 = pendingTimelocks?.filter(_temp3) ?? [];
    $[10] = pendingTimelocks;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const performanceFeeRecipientPendingTimelocks = t6;
  let t7;
  if ($[12] !== pendingTimelocks) {
    t7 = pendingTimelocks?.filter(_temp4) ?? [];
    $[12] = pendingTimelocks;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const managementFeeRecipientPendingTimelocks = t7;
  let t8;
  if ($[14] !== performanceFeeData?.fee || $[15] !== performanceFeePendingTimelocks) {
    t8 = performanceFeePendingTimelocks.length > 0 && <VaultV2PendingTimelocksIndicator pendingTimelocks={performanceFeePendingTimelocks} currentPerformanceFee={performanceFeeData?.fee} />;
    $[14] = performanceFeeData?.fee;
    $[15] = performanceFeePendingTimelocks;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== formattedPerformanceFee || $[18] !== isLoading) {
    t9 = isLoading ? <Skeleton className="h-md w-[60px]" /> : <span className="text-foreground text-xs">{formattedPerformanceFee}</span>;
    $[17] = formattedPerformanceFee;
    $[18] = isLoading;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t8 || $[21] !== t9) {
    t10 = <VaultV2RowListRow label="Performance fee" tooltip="The performance fee is a percentage of the yield generated by the vault, aligning the curator's revenue with the vault's success." labelAddon={t8}>{t9}</VaultV2RowListRow>;
    $[20] = t8;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== managementFeeData?.fee || $[24] !== managementFeePendingTimelocks) {
    t11 = managementFeePendingTimelocks.length > 0 && <VaultV2PendingTimelocksIndicator pendingTimelocks={managementFeePendingTimelocks} currentManagementFee={managementFeeData?.fee} />;
    $[23] = managementFeeData?.fee;
    $[24] = managementFeePendingTimelocks;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== formattedManagementFee || $[27] !== isLoading) {
    t12 = isLoading ? <Skeleton className="h-md w-[60px]" /> : <span className="text-foreground text-xs">{formattedManagementFee}</span>;
    $[26] = formattedManagementFee;
    $[27] = isLoading;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== t11 || $[30] !== t12) {
    t13 = <VaultV2RowListRow label="Management fee" tooltip="The management fee is a continuous fee charged on the vault's total assets under management (AUM), providing a steady revenue stream for the vault's managers." labelAddon={t11}>{t12}</VaultV2RowListRow>;
    $[29] = t11;
    $[30] = t12;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== performanceFeeRecipient || $[33] !== performanceFeeRecipientPendingTimelocks) {
    t14 = performanceFeeRecipientPendingTimelocks.length > 0 && <VaultV2PendingTimelocksIndicator pendingTimelocks={performanceFeeRecipientPendingTimelocks} currentPerformanceFeeRecipient={performanceFeeRecipient} />;
    $[32] = performanceFeeRecipient;
    $[33] = performanceFeeRecipientPendingTimelocks;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== chainId || $[36] !== isLoading || $[37] !== performanceFeeRecipient) {
    t15 = isLoading ? <Skeleton className="h-md w-[60px]" /> : chainId && <AddressWithCopyAndExplorer address={performanceFeeRecipient} chainId={chainId} />;
    $[35] = chainId;
    $[36] = isLoading;
    $[37] = performanceFeeRecipient;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== t14 || $[40] !== t15) {
    t16 = <VaultV2RowListRow label="Performance fee recipient" tooltip="The address that receives the performance fee from the vault." labelAddon={t14}>{t15}</VaultV2RowListRow>;
    $[39] = t14;
    $[40] = t15;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  let t17;
  if ($[42] !== managementFeeRecipient || $[43] !== managementFeeRecipientPendingTimelocks) {
    t17 = managementFeeRecipientPendingTimelocks.length > 0 && <VaultV2PendingTimelocksIndicator pendingTimelocks={managementFeeRecipientPendingTimelocks} currentManagementFeeRecipient={managementFeeRecipient} />;
    $[42] = managementFeeRecipient;
    $[43] = managementFeeRecipientPendingTimelocks;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  let t18;
  if ($[45] !== chainId || $[46] !== isLoading || $[47] !== managementFeeRecipient) {
    t18 = isLoading ? <Skeleton className="h-md w-[60px]" /> : chainId && <AddressWithCopyAndExplorer address={managementFeeRecipient} chainId={chainId} />;
    $[45] = chainId;
    $[46] = isLoading;
    $[47] = managementFeeRecipient;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  let t19;
  if ($[49] !== t17 || $[50] !== t18) {
    t19 = <VaultV2RowListRow label="Management fee recipient" tooltip="The address that receives the management fee from the vault." labelAddon={t17}>{t18}</VaultV2RowListRow>;
    $[49] = t17;
    $[50] = t18;
    $[51] = t19;
  } else {
    t19 = $[51];
  }
  let t20;
  if ($[52] !== t10 || $[53] !== t13 || $[54] !== t16 || $[55] !== t19) {
    t20 = <VaultV2RowListSection title="Fees" id="fees-section" className="scroll-mt-[calc(var(--sticky-offset,0px)+16px)]">{t10}{t13}{t16}{t19}</VaultV2RowListSection>;
    $[52] = t10;
    $[53] = t13;
    $[54] = t16;
    $[55] = t19;
    $[56] = t20;
  } else {
    t20 = $[56];
  }
  return t20;
};
export default memo(VaultV2PerformanceFees);
function _temp(timelock) {
  return timelock.functionName === VaultV2TimelockedFunctionName.SetPerformanceFee;
}
function _temp2(timelock_0) {
  return timelock_0.functionName === VaultV2TimelockedFunctionName.SetManagementFee;
}
function _temp3(timelock_1) {
  return timelock_1.functionName === VaultV2TimelockedFunctionName.SetPerformanceFeeRecipient;
}
function _temp4(timelock_2) {
  return timelock_2.functionName === VaultV2TimelockedFunctionName.SetManagementFeeRecipient;
}